/**
 * Quiz confetti engine shared by the DMM and USCN quiz experiences.
 *
 * Why this matters: both quizzes need the same reliable canvas lifecycle,
 * click-origin math, reduced-motion behavior, cleanup, palette, and botanical
 * particle shapes.
 */
import confetti from 'canvas-confetti';

export type QuizConfettiBrand = 'dmm' | 'uscn';
export type QuizBurstKind =
  | 'option'
  | 'multi-option'
  | 'submit'
  | 'milestone'
  | 'final-answer';

/** The top acknowledgment remains readable for exactly 2.5 seconds. */
export const QUIZ_ACK_DURATION_MS = 2500;

// DMM intentionally uses the exact USCN particle palette for quiz parity.
const DMM_COLORS = ['#4F7A2A', '#A8E063', '#FF8C42', '#FFD93D', '#FFF5DD'];
const USCN_COLORS = ['#4F7A2A', '#A8E063', '#FF8C42', '#FFD93D', '#FFF5DD'];
const DMM_FINALE_COLORS = ['#4F7A2A', '#A8E063', '#FF8C42', '#FFD93D', '#FFB5BA', '#C41E3A'];
const USCN_FINALE_COLORS = ['#4F7A2A', '#A8E063', '#FF8C42', '#FFD93D', '#FFB5BA', '#C41E3A'];

// Both brands intentionally share the same abstract, single-weight leaf and
// citrus blossom silhouettes for quiz parity.
const LEAF_PATH =
  'M2 17C2.5 8.5 8 2.5 18 2C18 12 12.5 18 3 18C6 13 10 8.5 15 5';
const BLOSSOM_PATH =
  'M10 9C3 8 2 2 7 1C10 0 11 4 10 9ZM11 9C12 2 18 1 19 6C20 9 16 11 11 9ZM11 10C18 9 20 15 16 18C13 20 10 16 11 10ZM10 10C9 17 3 19 1 14C0 11 5 9 10 10Z';

interface BurstConfig {
  particleCount: number;
  spread: number;
  startVelocity: number;
  ticks: number;
  scalar: number;
  gravity: number;
}

const BURST_CONFIGS: Record<QuizBurstKind, BurstConfig> = {
  'multi-option': {
    particleCount: 7,
    spread: 42,
    startVelocity: 18,
    ticks: 75,
    scalar: 0.72,
    gravity: 0.82,
  },
  option: {
    particleCount: 14,
    spread: 58,
    startVelocity: 24,
    ticks: 100,
    scalar: 0.82,
    gravity: 0.86,
  },
  submit: {
    particleCount: 24,
    spread: 80,
    startVelocity: 30,
    ticks: 125,
    scalar: 0.92,
    gravity: 0.84,
  },
  milestone: {
    particleCount: 32,
    spread: 100,
    startVelocity: 34,
    ticks: 145,
    scalar: 1,
    gravity: 0.8,
  },
  'final-answer': {
    particleCount: 42,
    spread: 125,
    startVelocity: 38,
    ticks: 165,
    scalar: 1.05,
    gravity: 0.76,
  },
};

let confettiInstance: confetti.CreateTypes | null = null;
let botanicalShapes: Partial<Record<QuizConfettiBrand, confetti.Shape[]>> = {};
const pendingTimers = new Set<number>();

function supportsBrowserConfetti(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Browser and library checks both honor reduced motion. The explicit check
 * avoids even creating a canvas for someone who asked for less animation.
 */
export function prefersReducedQuizMotion(): boolean {
  if (!supportsBrowserConfetti() || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getConfettiInstance(): confetti.CreateTypes | null {
  if (!supportsBrowserConfetti() || prefersReducedQuizMotion()) return null;
  if (!confettiInstance) {
    confettiInstance = confetti.create(undefined, {
      resize: true,
      useWorker: true,
      disableForReducedMotion: true,
    });
  }
  return confettiInstance;
}

function getBotanicalShapes(brand: QuizConfettiBrand): confetti.Shape[] {
  const cached = botanicalShapes[brand];
  if (cached) return cached;

  try {
    const leaf = confetti.shapeFromPath(LEAF_PATH);
    const accent = confetti.shapeFromPath(BLOSSOM_PATH);
    const shapes: confetti.Shape[] = [leaf, accent, 'circle'];
    botanicalShapes = { ...botanicalShapes, [brand]: shapes };
    return shapes;
  } catch (error) {
    // Path2D/DOMMatrix may be missing in an older browser. The celebration
    // still works with native shapes instead of failing the quiz interaction.
    console.warn('Quiz confetti custom shapes unavailable; using native shapes.', error);
    const fallback: confetti.Shape[] = ['circle', 'star'];
    botanicalShapes = { ...botanicalShapes, [brand]: fallback };
    return fallback;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Convert the clicked card/button center into canvas-confetti's normalized
 * viewport coordinates, keeping particles away from hard screen edges.
 */
export function getQuizConfettiOrigin(source: HTMLElement | null): confetti.Origin {
  if (!source || !supportsBrowserConfetti()) return { x: 0.5, y: 0.55 };
  const rect = source.getBoundingClientRect();
  const viewportWidth = Math.max(window.innerWidth, 1);
  const viewportHeight = Math.max(window.innerHeight, 1);
  return {
    x: clamp((rect.left + rect.width / 2) / viewportWidth, 0.06, 0.94),
    y: clamp((rect.top + rect.height / 2) / viewportHeight, 0.12, 0.9),
  };
}

function safeFire(options: confetti.Options): void {
  try {
    // Why this matters: canvas/worker initialization can throw before firing,
    // so creation and invocation must share the same fail-open boundary.
    const instance = getConfettiInstance();
    if (!instance) return;
    void instance({
      zIndex: 9999,
      disableForReducedMotion: true,
      decay: 0.92,
      ...options,
    });
  } catch (error) {
    // Confetti is delight, never a blocker. Keep diagnostic context without
    // interrupting answer selection or quiz navigation.
    console.error('Quiz confetti animation failed:', error);
  }
}

/**
 * Fire a botanical burst from the exact option/button the person clicked.
 * Submit and milestone kinds use a two-direction fan for a more special beat.
 */
export function fireQuizBotanicalBurst(
  source: HTMLElement | null,
  brand: QuizConfettiBrand,
  kind: QuizBurstKind
): void {
  if (prefersReducedQuizMotion()) return;

  const config = BURST_CONFIGS[kind];
  const origin = getQuizConfettiOrigin(source);
  const colors = brand === 'dmm' ? DMM_COLORS : USCN_COLORS;
  const shapes = getBotanicalShapes(brand);

  if (kind === 'submit' || kind === 'milestone') {
    const count = Math.ceil(config.particleCount / 2);
    safeFire({
      ...config,
      particleCount: count,
      origin,
      angle: 70,
      drift: -0.15,
      colors,
      shapes,
    });
    safeFire({
      ...config,
      particleCount: count,
      origin,
      angle: 110,
      drift: 0.15,
      colors,
      shapes,
    });
    return;
  }

  safeFire({
    ...config,
    origin,
    angle: 90,
    colors,
    shapes,
  });
}

function scheduleFire(delay: number, options: confetti.Options): void {
  const timer = window.setTimeout(() => {
    pendingTimers.delete(timer);
    safeFire(options);
  }, delay);
  pendingTimers.add(timer);
}

/**
 * Results finale: colorful side cannons, a central star burst, then a softer
 * botanical shower. It is deliberately reserved for quiz completion.
 */
export function fireQuizFinale(brand: QuizConfettiBrand): void {
  if (!supportsBrowserConfetti() || prefersReducedQuizMotion()) return;
  const colors = brand === 'dmm' ? DMM_FINALE_COLORS : USCN_FINALE_COLORS;

  safeFire({
    particleCount: 55,
    angle: 60,
    spread: 58,
    startVelocity: 52,
    decay: 0.91,
    gravity: 0.9,
    ticks: 190,
    scalar: 1,
    origin: { x: 0, y: 0.72 },
    colors,
    shapes: ['square', 'circle'],
  });
  safeFire({
    particleCount: 55,
    angle: 120,
    spread: 58,
    startVelocity: 52,
    decay: 0.91,
    gravity: 0.9,
    ticks: 190,
    scalar: 1,
    origin: { x: 1, y: 0.72 },
    colors,
    shapes: ['square', 'circle'],
  });

  scheduleFire(180, {
    particleCount: 65,
    spread: 105,
    startVelocity: 42,
    gravity: 0.78,
    ticks: 210,
    scalar: 1.08,
    origin: { x: 0.5, y: 0.58 },
    colors,
    shapes: ['star', 'circle'],
  });

  scheduleFire(430, {
    particleCount: 35,
    spread: 145,
    startVelocity: 28,
    gravity: 0.65,
    ticks: 220,
    scalar: 0.9,
    origin: { x: 0.5, y: 0.18 },
    colors,
    shapes: getBotanicalShapes(brand),
  });
}

/**
 * Stop all queued/active particles. Results components call this on unmount,
 * preventing canvases or timers from leaking into a retake. The browser guard
 * keeps this cleanup safe during server rendering and pure Node tests.
 */
export function resetQuizConfetti(): void {
  if (typeof window !== 'undefined') {
    pendingTimers.forEach((timer) => window.clearTimeout(timer));
  }
  pendingTimers.clear();
  confettiInstance?.reset();
  confettiInstance = null;
}
